import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { Art57Node } from '../../ui/Node/Art57Node/Art57Node';
import { Art57NodeUtils } from '../../ui/Node/Art57Node/Art57NodeUtils';
import { TreeNodeXEVPRMImportedMessage } from '../../ui/Node/WebTraderNode/Entity/TreeNodeXEVPRMImportedMessage';
import { AuthService } from '../auth-service/auth-service.service';
import { CodeName } from './code-name.model';
import { LoggerService } from '../logger-service/logger.service';

function lookupListUrl(
  lookup: string
): string {
  let url = `/api/lookups/${lookup}`;
  return url;
}

function lookupCodeUrl(
  lookup: string,
  code: string
): string {
  let url = `/api/lookups/${lookup}/${code}`;
  return url;
}


@Injectable({
  providedIn: 'root',
})
export class LookupGetService {
  /**
   * Cache results for the get(lookup, code) and
   * getAll(lookup, code) operations
   */
  private cache: Map<string, any> = new Map();
  private nodes!: Art57Node[];
  private localNumberSequence: number = 0;
  private filenameNumberSequence: number = 0;

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private injector: Injector,
    private loggerService: LoggerService
  ) { }

  addToCache(lookup: string, values: any) {
    let transformedValues: any; 
    let defaultValue: CodeName;

    switch(lookup) {
      // convert code to number
      case 'operationtype':
      case 'operationtypeoperationresult':
      case 'productstatus':
      case 'productversion':
      case 'medicinalproducttype':
      case 'medicaldevice':
      case 'legalbasis':
      case 'authorisationprocedure':   
      case 'operationresult':
      case 'supportedbacklogmeddraversions':
      case 'supportedmeddraversions':        
        transformedValues = values.map((item: any) => ({
        ...item,
        code: Number(item.code)
      }));
        break;
      // add default value converting code to number (review, empty value conversion, now is working)
      case 'amountvaluetype':
      case 'validauthorisationstatus':
      case 'notvalidauthorisationstatus': 
      case 'authorisationstatus': 
        defaultValue = new CodeName();
        defaultValue.code = '';
        defaultValue.name = '';
        transformedValues = values.map((item: any) => ({
         ...item,
         code: Number(item.code)
        }));
        transformedValues = [defaultValue, ...transformedValues];
        break;
      // add default value without converting code to number
      case 'country': 
      case 'countryeu':
      case 'countryeea':
      case 'countrynoneea': 
      case 'unitofpresentation': 
      case 'unitofmeasurement':
        defaultValue = new CodeName();
        defaultValue.code = '';
        defaultValue.name = '';
        transformedValues = [defaultValue, ...values];
        break;
      default:
        transformedValues = values;
        break;
    }

    this.cache.set(lookupListUrl(lookup), transformedValues);
  }

  addToValueCache(lookupValue: string, value: any) {
    this.cache.set(lookupListUrl(lookupValue), value);
  }

  clearCache(): void {
    this.cache.clear();
  }

  //Method to load options and cache them
  // Update retrieveFromCache to specify a return type
  retrieveFromCache(lookup: string): Observable<CodeName[]> {
    const cachedValues = this.cache.get(lookupListUrl(lookup));
    return cachedValues ? of(cachedValues as CodeName[]) : of([]);
  }

  retrieveValueFromCache(lookup: string, code: any): string {
    let value: any;
    let values: any[] = this.cache.get(lookupListUrl(lookup));
    if (values != null) {
      value = values.find((v) => v.code == code);
    }
    return value ? value.name : '';
  }

  retrieveFromValueCache(lookupValue: string): CodeName | null {
    const cachedValue = this.cache.get(lookupListUrl(lookupValue));
    return cachedValue ? cachedValue as CodeName : null;
  }

  /**
   * Looks up a code and caches the result to avoid a round
   * trip on the next invocation.
   *
   * @param lookup Name of lookup
   * @param code Code to lookup
   * @returns Looked up entry
   */
  get(lookup: string, code: string): Observable<any> {
    return this.getImpl<CodeName>(lookupCodeUrl(lookup, code));
  }

  getLookupValue(lookup: string, code: string): Observable<CodeName> {
    const defaultCodeName: CodeName = { code: '', name: '' };

    return this.get(lookup, code)
      .pipe(
        map(result => result || of(defaultCodeName)),
        catchError(() => of(defaultCodeName))
      );
  }

  async getLookup(lookup: string, code: string): Promise<CodeName> {
    const defaultCodeName: CodeName = { code: '', name: '' };

    return new Promise<CodeName>((resolve) => {
      this.get(lookup, code)
        .pipe(
          map(result => result || defaultCodeName),
          catchError(() => of(defaultCodeName))
        )
        .subscribe((result: CodeName) => {
          resolve(result);
        });
    });
  }

  /**
   * Gets all codes and caches the result to avoid a round
   * trip on the next invocation.
   * @param lookup Name of lookup
   * @returns List of looked up entries
   */
  getAll(lookup: string, parameters?: [string, string][]): Observable<CodeName[]> {
      return this.getImpl<CodeName[]>(lookupListUrl(lookup), parameters);
  }

  filter(lookup: string, filter: string, parameters?: [string, string][]): Observable<any[]> {
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken(),
    });

    let params = new HttpParams().set('filter', filter);
    parameters?.forEach((entry) => { params = params.append(entry[0], entry[1]) });

    return this.httpClient
      .get(`${environment.apiUrl}/api/lookups/${lookup}`, {
        headers: headers, params: params
      })
      .pipe(
        map((resp) => {
          const result = resp as [];
          return result;
        })
      );
  }

  /**
   * Gets all codes and caches the result to avoid a round
   * trip on the next invocation.
   *
   * @param url Name of lookup
   * @returns List of looked up entities
   */
  private getImpl<ResultType>(url: string, parameters?: [string, string][]): Observable<ResultType> {

    let params = new HttpParams();
    parameters?.forEach((entry) => { params = params.append(entry[0], entry[1]) });

    //const cached = this.cache.get(`${url}?${params.toString()}`);
    const key:string = `${url}${parameters&&parameters.length>0?'?'+params.toString():''}`;
    const cached = this.cache.get(key);
    if (cached) {
      return of(cached);
    }
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getAccessToken(),
      AuthSession: this.authService.getSessionToken(),
    });

    return this.httpClient
      .get(environment.apiUrl + url, { headers: headers, params: params })
      .pipe(
        map((resp) => {
          const result = resp as ResultType;
          this.addToCache(key, result);
          return result;
        })
      );
  }

  // Load options from cache
  public loadCachedOptions(lookup: string): Observable<CodeName[]> {
    return this.injector
      .get(LookupGetService)
      .retrieveFromCache(lookup)
      .pipe(
        switchMap((cachedOptions: CodeName[]) => {
          if (cachedOptions.length > 0) {
            return of(cachedOptions);
          } else {
            return this
              .getAll(lookup)
              .pipe(
                tap((options: CodeName[]) =>
                  this.injector
                    .get(LookupGetService)
                    .addToCache(lookup, options)
                )
              );
          }
        })
      );
  }

  /**
   * Add inline lookup values
   */
  addInlineValues(lookup: string, values: CodeName[]) {
    this.cache.set(lookupListUrl(lookup), values);
    for (let i = 0; i < values.length; i++) {
      this.cache.set(lookupCodeUrl(lookup, values[i].code), values[i]);
    }
  }

  public init(nodes: Art57Node[]) {
    this.nodes = nodes;
  }

  public getLocalLookup(name: string, parameters: any[]): any {
    // TODO: check if this method is used in CreateandSendProductReports only (because locallookup are set in local import backend)
    const messageNode = (this.injector.get(Art57Section).isCreateAndSendProductSection()) ? this.injector.get(Art57Section).getSelectedSection().treeView[0] :
      Art57NodeUtils.findFirstParentNodeOfType(this.injector.get(Art57Section).getSelectedSection().selectedNode, TreeNodeXEVPRMImportedMessage);
    const message: any = messageNode.getMessage();
    switch (name) {
      case 'mah':
        if (
          message &&
          message.organisations &&
          message.organisations.organisation
        )
          return message.organisations.organisation
            .filter((o: any) => o.type_Org == 1)
            .map((item: any) => ({
              code: item.localnumber,
              name: item.name_Org,
            }));
        else return null;

      case 'sponsor':
        if (
          message &&
          message.organisations &&
          message.organisations.organisation
        )
          return message.organisations.organisation
            .filter((o: any) => o.type_Org == 2)
            .map((item: any) => ({
              code: item.localnumber,
              name: item.name_Org,
            }));
        else return null;

      case 'mfl':
        if (
          message &&
          message.masterfilelocations &&
          message.masterfilelocations.masterfilelocation
        )
          return message.masterfilelocations.masterfilelocation.map(
            (item: any) => ({ code: item.localnumber, name: ((item.mflcountrycode && item.mflcountrycode.name) ? item.mflcountrycode.name : '') + ' - ' + item.mflcity })
          );
        else return null;

      case 'ppiattachment':
        if (message && message.attachments && message.attachments.attachment)
          return message.attachments.attachment
            .filter((a: any) => a.attachmenttype == 1)
            .map((item: any) => ({
              code: item.localnumber,
              name: item.attachmentname,
            }));
        else return null;

      case 'psiattachment':
        if (message && message.attachments && message.attachments.attachment)
          return message.attachments.attachment
            .filter((a: any) => a.attachmenttype == 2)
            .map((item: any) => ({
              code: item.localnumber,
              name: item.attachmentname,
            }));
        else return null;

      case 'atc':
        if (
          message &&
          message.standardterminology &&
          message.standardterminology.atcs &&
          message.standardterminology.atcs.atc &&
          parameters &&
          parameters.filter(i => i[0] === 'type').length > 0) {

          let types: string[] = (parameters.filter(i => i[0] === 'type')[0][1] as String).split('|');
          return message.standardterminology.atcs.atc.filter((n: any) => types.includes(n.type_Term.toString())).map((item: any) => ({
            code: item.atccode,
            name: item.atccode_Desc,
          }));

        } else if (
          message &&
          message.standardterminology &&
          message.standardterminology.atcs &&
          message.standardterminology.atcs.atc) {

          return message.standardterminology.atcs.atc.map((item: any) => ({
            code: item.atccode,
            name: item.atccode_Desc,
          }));

        }

        return null;


      case 'pharmform':

        if (
          message &&
          message.standardterminology &&
          message.standardterminology.pharmaceuticalforms &&
          message.standardterminology.pharmaceuticalforms.pharmaceuticalform &&
          parameters &&
          parameters.filter(i => i[0] === 'type').length > 0) {

          let types: string[] = (parameters.filter(i => i[0] === 'type')[0][1] as String).split('|');
          return message.standardterminology.pharmaceuticalforms.pharmaceuticalform.filter((n: any) => types.includes(n.type_Term.toString())).map((item: any) => ({
            code: item.localnumber,
            name: item.name_Pharmform,
          }));

        } else if (
          message &&
          message.standardterminology &&
          message.standardterminology.pharmaceuticalforms &&
          message.standardterminology.pharmaceuticalforms.pharmaceuticalform
        ) {
          return message.standardterminology.pharmaceuticalforms.pharmaceuticalform.map(
            (item: any) => ({
              code: item.localnumber,
              name: item.name_Pharmform,
            })
          );
        }
        return null;
      case 'adminroute':

        if (
          message &&
          message.standardterminology &&
          message.standardterminology.administrationroutes &&
          message.standardterminology.administrationroutes.administrationroute &&
          parameters &&
          parameters.filter(i => i[0] === 'type').length > 0) {

          let types: string[] = (parameters.filter(i => i[0] === 'type')[0][1] as String).split('|');
          return message.standardterminology.administrationroutes.administrationroute.filter((n: any) => types.includes(n.type_Term.toString())).map((item: any) => ({
            code: item.localnumber,
            name: item.name_Admroute,
          }));


        } else if (
          message &&
          message.standardterminology &&
          message.standardterminology.administrationroutes &&
          message.standardterminology.administrationroutes.administrationroute
        ) {
          return message.standardterminology.administrationroutes.administrationroute.map(
            (item: any) => ({
              code: item.localnumber,
              name: item.name_Admroute,
            })
          );

        }

        return null;



      case 'source':
        if (message && message.sources && message.sources.source)
          return message.sources.source.map((item: any) => ({
            code: item.localnumber,
            name: item.sourcename,
          }));
        else return null;
      case 'substance':

        let substances: any[] = [];
        if (
          message &&
          message.approvedsubstances &&
          message.approvedsubstances.approvedsubstance
        )
          substances = substances.concat(message.approvedsubstances.approvedsubstance.map(
            (item: any) => ({
              code: item.localnumber,
              name: item.substancename,
            })
          ));

        if (
          message &&
          message.developmentsubstances &&
          message.developmentsubstances.developmentsubstance
        )

          substances = substances.concat(message.developmentsubstances.developmentsubstance.map(
            (item: any) => ({
              code: item.localnumber,
              name: item.substancename,
            })
          ));

        return (substances && substances.length == 0) ? null : substances;


      default:
        return null;

    }
  }

  private requestQueue: (() => void)[] = [];
  private activeRequests = 0;
  private maxConcurrentRequests = 5;

  private enqueueRequest(requestTask: () => void): void {
    this.requestQueue.push(requestTask);
    this.processQueue();
  }

  private processQueue(): void {
    while (this.activeRequests < this.maxConcurrentRequests && this.requestQueue.length > 0) {
      const nextRequest = this.requestQueue.shift();
      if (nextRequest) {
        this.activeRequests++;
        nextRequest();
      }
    }
  }

  public getLookupValueSync(lookupName: string, value: string): CodeName {

    let result: CodeName = { code: "", name: "" };

    let requestTask = null
    let cachedResult: CodeName | null = this.retrieveFromValueCache(`${lookupName}/${value}`);
    if (cachedResult) {
      result = cachedResult;
    } else {
      requestTask = () => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${environment.apiUrl}/api/lookups/${lookupName}/${value}`, false);
        xhr.setRequestHeader('Accept', 'application/json, text/plain, */*');
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.authService.getAccessToken());
        xhr.setRequestHeader('AuthSession', this.authService.getSessionToken());
        xhr.send();
        if (xhr.status === 200) {
          if (xhr.response != null && xhr.response != '') {
            result = JSON.parse(xhr.response) as CodeName;
            this.addToValueCache(`${lookupName}/${value}`, result);
          } else {
            this.addToValueCache(`${lookupName}/${value}`, { code: "", name: "" });
          }
        } else {
          this.loggerService.err(`Error fetching data: ${xhr.status}`);
        }
        this.activeRequests--;
        this.processQueue();
      };
    }

    if (requestTask) {
      this.enqueueRequest(requestTask);
    }

    return result;
  }

  fixLocalNumber(importedLocalNumber: any) {
    let localNumbernumber;

    if (typeof importedLocalNumber === "string") {
      localNumbernumber = Number(importedLocalNumber);
    } else {
      localNumbernumber = importedLocalNumber;
    }

    if (!isNaN(localNumbernumber)){
      if (this.localNumberSequence < localNumbernumber) {
        this.localNumberSequence = localNumbernumber;
      }
    }  
  }

  getNodeLocalNumber(clonedNode: any, originalNode: any):any {
    let localNumer = undefined;
    let convertedLocalNumber = undefined;
    
    if (clonedNode.parent === originalNode.parent) {
      localNumer = clonedNode.data.model.localnumber?clonedNode.data.model.localnumber:this.injector.get(LookupGetService).getLocalNumber();
    }else {
      localNumer = originalNode.data.model.localnumber;
    }

    if (typeof localNumer === "string") {
      convertedLocalNumber = Number(localNumer);
    } else {
      convertedLocalNumber = localNumer;
    }

    if (!isNaN(convertedLocalNumber)) {
      return convertedLocalNumber;
    } else {
      return localNumer;
    }


  }

  getLocalNumber(): number {
    this.localNumberSequence = this.localNumberSequence + 1;
    return this.localNumberSequence;
  }

  getFilenameNumber(): number {
    this.filenameNumberSequence = this.filenameNumberSequence + 1;
    return this.filenameNumberSequence;
  }

  resetLocalNumberSequences() {
    this.localNumberSequence = 0;
    this.filenameNumberSequence = 0;
  }
}
